/** Snagging register API wrapper. Stateless: the hook owns the list. */

import { SUPABASE_URL } from '../utils/supabase/url';
import { getAuthHeaders } from '../utils/supabase/client';
import type { ProjectSnag, SnagInput, SnagStatus } from '../types/projectSnag';

const BASE = `${SUPABASE_URL}/functions/v1/make-server-a476273b/project-snags/projects`;

async function request<T>(path: string, init?: { method?: string; json?: unknown }): Promise<T> {
  const headers = await getAuthHeaders({ 'Content-Type': 'application/json' });
  const res = await fetch(`${BASE}${path}`, {
    method: init?.method,
    headers,
    body: init?.json !== undefined ? JSON.stringify(init.json) : undefined,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

export interface OutstandingSnagTally { open: number; fixed: number; overdue: number }

const OUTSTANDING_URL = `${SUPABASE_URL}/functions/v1/make-server-a476273b/project-snags/outstanding`;

const root = (projectId: string) => `/${encodeURIComponent(projectId)}`;

export const ProjectSnagService = {
  /** Snags not yet signed off, per project the caller may read, for one workspace. */
  outstanding: async (workspaceId: string): Promise<Record<string, OutstandingSnagTally>> => {
    const res = await fetch(`${OUTSTANDING_URL}?workspaceId=${encodeURIComponent(workspaceId)}`, { headers: await getAuthHeaders() });
    if (!res.ok) throw new Error(`Request failed (${res.status})`);
    return ((await res.json()) as { counts: Record<string, OutstandingSnagTally> }).counts;
  },

  list: (projectId: string) => request<{ snags: ProjectSnag[]; readOnly: boolean }>(root(projectId)),

  create: async (projectId: string, input: SnagInput): Promise<ProjectSnag> =>
    (await request<{ snag: ProjectSnag }>(root(projectId), { method: 'POST', json: input })).snag,

  update: async (projectId: string, id: string, patch: SnagInput & { status?: SnagStatus }): Promise<ProjectSnag> =>
    (await request<{ snag: ProjectSnag }>(`${root(projectId)}/${encodeURIComponent(id)}`, { method: 'PATCH', json: patch })).snag,

  remove: (projectId: string, id: string) =>
    request<{ success: true }>(`${root(projectId)}/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};
